import type { IPresentationThemeExtractionNotificationData } from "@frontend-shared/types";
import { AlertTriangle, Palette } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";

interface IPresentationThemeExtractionCardProps {
  uuid: string;
  data: IPresentationThemeExtractionNotificationData;
  createdAt: string;
  isRead: boolean;
  isSuccess: boolean;
  onViewAppearance: (organizationUuid: string) => void;
}

// NOTE: Shared card for both PRESENTATION_THEME_READY and _FAILED (NSAI-3862)
// — same data shape and CTA (the appearance settings tab), only the icon and
// body copy differ.
const PresentationThemeExtractionCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  isSuccess,
  onViewAppearance,
}: IPresentationThemeExtractionCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewAppearance = useCallback(
    () => onViewAppearance(data.organizationUuid),
    [onViewAppearance, data.organizationUuid],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId={
        isSuccess ? "presentation_theme_ready" : "presentation_theme_failed"
      }
      createdAt={createdAt}
      isRead={isRead}
      icon={
        isSuccess ? (
          <Palette className="h-5 w-5 text-emerald-500" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-red-500" />
        )
      }
      iconBgClassName={isSuccess ? "bg-emerald-500/10" : "bg-red-500/10"}
      ctaLabel={t("view_presentation_theme")}
      onCtaClick={handleViewAppearance}
      body={
        isSuccess
          ? t("type.presentation_theme_ready.body", {
              fileName: data.fileName,
            })
          : t("type.presentation_theme_failed.body", {
              fileName: data.fileName,
              reason: data.reason ?? "",
            })
      }
    />
  );
};

export const PresentationThemeExtractionCard = memo(
  PresentationThemeExtractionCardComponent,
);
